import {
	QueryFunctionContext,
	useInfiniteQuery,
	useMutation,
	useQuery,
} from '@tanstack/react-query';
import { CreateSiteRequest, UpdateSiteRequest } from '@/api/sites/types';
import {
	createSite,
	exportSiteUsers,
	getSite,
	getSiteDevices,
	getSites,
	updateSite,
} from '@/api/sites';
import { MutationRequest, PaginationQueryParams } from '@/api/types';
import { UseMutationQueryParams, UpdateMutationFnParams } from './types';

const siteKeys = {
	allCustomerSites: () => ['all_customer_sites'] as const,
	allSiteDevices: () => ['all_site_devices'] as const,
	singleSite: () => ['single_site'] as const,
};

export const useCreateSite = (mutationQueryParams?: UseMutationQueryParams) =>
	useMutation({
		mutationFn: ({ id, request }: { id: string; request: MutationRequest<CreateSiteRequest> }) =>
			createSite(id, request),
		...mutationQueryParams,
	});

export const useUpdateSite = (mutationQueryParams?: UseMutationQueryParams) =>
	useMutation({
		mutationFn: ({ id, request }: UpdateMutationFnParams<UpdateSiteRequest>) =>
			updateSite(id, request),
		...mutationQueryParams,
	});

interface UseSitesParams {
	customerId?: string;
	queryParams?: {
		limit?: number;
		search?: string;
	};
}

export const useSites = ({ customerId, queryParams }: UseSitesParams) => {
	const queryKey = [...siteKeys.allCustomerSites(), customerId!, queryParams];
	const query = useInfiniteQuery({
		queryKey: [...siteKeys.allCustomerSites(), customerId, queryParams],
		queryFn: ({
			pageParam = 1,
		}: QueryFunctionContext<(string | { search?: string } | undefined)[], number>) =>
			getSites(customerId!, { ...queryParams, page: pageParam }),
		enabled: !!customerId,
		refetchOnWindowFocus: false,
		keepPreviousData: true,
		getNextPageParam: ({ page, totalPages }) => (page === totalPages ? undefined : page + 1),
	});

	return { queryKey, query };
};

export const useSiteDevices = (siteId?: string, queryParams?: PaginationQueryParams) => {
	const queryKey = [...siteKeys.allSiteDevices(), siteId, queryParams];
	const query = useInfiniteQuery({
		queryKey,
		queryFn: ({
			pageParam = 1,
		}: QueryFunctionContext<(string | { search?: string } | undefined)[], number>) =>
			getSiteDevices(siteId!, { ...queryParams, page: pageParam }),
		enabled: !!siteId,
		refetchOnWindowFocus: false,
		keepPreviousData: true,
		getNextPageParam: ({ page, totalPages }) => (page === totalPages ? undefined : page + 1),
	});

	return { queryKey, query };
};

export const useSite = (id?: string, enabled = true) => {
	const queryKey = [...siteKeys.singleSite(), id];
	const query = useQuery({
		queryKey,
		queryFn: () => getSite(id!),
		enabled: !!id && enabled,
		refetchOnWindowFocus: false,
	});

	return { queryKey, query };
};

export const useExportSiteUsers = (mutationQueryParams?: UseMutationQueryParams) =>
	useMutation({
		mutationFn: (siteId: string) => exportSiteUsers(siteId),
		...mutationQueryParams,
	});
